import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type StreamData } from "./api";
import { useDashboardStore, type StreamStats } from "./store";
import { ChunkMap } from "./ChunkMap";
import { Player } from "./Player";

/** Recent announcement blocks from the live energy gate ("who's dancing now") */
function LiveAnnouncements({ streamKey }: { streamKey: string }) {
    const { data } = useQuery({
        queryKey: ["liveBlocks", streamKey],
        queryFn: () => api.liveBlocks(streamKey),
        refetchInterval: 15_000,
        staleTime: 10_000,
    });
    if (!data || (!data.gate && data.blocks.length === 0)) return null;

    const fmt = (ms: number) => {
        const s = Math.floor(ms / 1000);
        return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    };
    return (
        <div style={{ marginTop: 10, fontSize: "0.8em" }}>
            <h3 style={{ fontSize: "1em", marginBottom: 4 }}>
                announcements
                {data.gate && <span style={{ color: data.gate.state === "music" ? "#33aa55" : "#aa8833", marginLeft: 8 }}>
                    ({data.gate.state} since {fmt(data.gate.sinceMs)})
                </span>}
            </h3>
            {data.blocks.length === 0 && <div style={{ color: "#555" }}>no announcement blocks yet</div>}
            {data.blocks.slice(0, 5).map(b => (
                <div key={b.id} style={{ padding: "3px 0", borderBottom: "1px solid #222" }}>
                    <span style={{ color: "#888", fontVariantNumeric: "tabular-nums" }}>{fmt(b.t0_ms)}–{fmt(b.t1_ms)}</span>{" "}
                    {b.status === "done"
                        ? <span style={{ color: "#e0e0e0" }}>{b.text || <i style={{ color: "#555" }}>(no speech found)</i>}</span>
                        : <span style={{ color: b.status === "error" ? "#aa3333" : "#aa8833" }}>{b.status === "error" ? "failed" : "transcribing…"}</span>}
                </div>
            ))}
        </div>
    );
}

function total(s: StreamStats | undefined): number {
    if (!s) return 0;
    return s.pending + s.downloading + s.stored + s.failed + s.expired;
}

export function StreamCard({ stream: s, expanded, playing, onToggle, onPlay, onStop, onRetry }: {
    stream: StreamData;
    expanded: boolean;
    playing: boolean;
    onToggle: () => void;
    onPlay: () => void;
    onStop: () => void;
    onRetry: () => void;
}) {
    const isLive = s.status === "active";
    const playerSeek = useRef<(time: number) => void>(() => {});
    const vs = useDashboardStore(st => st.stats[s.stream_key]);
    const as = useDashboardStore(st => st.stats[s.stream_key + "-audio"]);

    const vStored = vs?.stored || 0;
    const vPending = vs?.pending || 0;
    const vFailed = vs?.failed || 0;
    const vExpired = vs?.expired || 0;
    const vDownloading = vs?.downloading || 0;

    const aStored = as?.stored || 0;
    const aPending = as?.pending || 0;
    const aFailed = as?.failed || 0;
    const aDownloading = as?.downloading || 0;

    const storedN = vStored;
    const pendingN = vPending + aPending + vDownloading + aDownloading;
    const failedN = vFailed + aFailed;
    const expiredN = vExpired;
    const vTotal = total(vs);

    const sizeMB = ((vs?.totalSize || 0) / 1024 / 1024).toFixed(0);
    const durMin = ((vs?.totalDuration || 0) / 60).toFixed(1);
    const hasChunks = vStored > 0;

    const totalExpected = vTotal + total(as);
    const totalStored = vStored + aStored;
    const pct = totalExpected > 0 ? ((totalStored / totalExpected) * 100).toFixed(0) : "0";

    const displayName = s.title || s.stream_key;

    return (
        <div className={`stream ${isLive ? "active" : ""} ${expanded ? "selected" : ""}`}>
            <div onClick={onToggle} style={{ cursor: "pointer" }}>
                <div className="stream-header">
                    <span className="stream-key">
                        {isLive && <span className="live-dot" />}
                        {displayName}
                        {s.event_id && <span style={{ color: "#555", fontSize: "0.75em", marginLeft: 6 }}>#{s.event_id}</span>}
                    </span>
                    <span>
                        {s.thumb_s3_key && (
                            <img
                                src={`/api/thumb/${encodeURIComponent(s.stream_key)}`}
                                style={{ width: 80, height: 45, objectFit: "cover", borderRadius: 4, marginRight: 8, verticalAlign: "middle" }}
                                loading="lazy"
                            />
                        )}
                        {hasChunks && (
                            <button className="btn btn-sm btn-green" onClick={e => { e.stopPropagation(); onPlay(); }} style={{ marginRight: 4 }}>
                                {playing ? "close" : "play"}
                            </button>
                        )}
                        {(isLive || s.running) && (
                            <button className="btn btn-sm btn-red" onClick={e => { e.stopPropagation(); onStop(); }} style={{ marginRight: 4 }}>stop</button>
                        )}
                        {(failedN > 0 || expiredN > 0) && (
                            <button className="btn btn-sm btn-yellow" onClick={e => { e.stopPropagation(); onRetry(); }} style={{ marginRight: 4 }}>retry</button>
                        )}
                        <span className={`badge ${s.status}`}>{s.status}</span>
                    </span>
                </div>
                <div className="stats">
                    <div className="stat"><span className="stat-val">{storedN}</span><span className="stat-label">stored</span></div>
                    <div className="stat"><span className="stat-val">{pendingN}</span><span className="stat-label">pending</span></div>
                    <div className="stat"><span className="stat-val">{sizeMB} MB</span><span className="stat-label">size</span></div>
                    <div className="stat"><span className="stat-val">{durMin} min</span><span className="stat-label">duration</span></div>
                    <div className="stat">
                        <span className="stat-val">{isLive ? totalExpected : `${pct}%`}</span>
                        <span className="stat-label">{isLive ? "known" : "complete"}</span>
                    </div>
                    {(pendingN > 0 || failedN > 0) && (
                        <div className="stat">
                            <span className="stat-val" style={{ color: failedN > 0 ? "#aa3333" : "#aa8833" }}>
                                {pendingN > 0 ? pendingN : ""}{pendingN > 0 && failedN > 0 ? "/" : ""}{failedN > 0 ? failedN : ""}
                            </span>
                            <span className="stat-label">{failedN > 0 ? "pend/fail" : "remaining"}</span>
                        </div>
                    )}
                </div>
                <div className="progress-bar">
                    <div className="pb-stored" style={{ width: `${vTotal ? storedN / vTotal * 100 : 0}%` }} />
                    <div className="pb-pending" style={{ width: `${vTotal ? pendingN / vTotal * 100 : 0}%` }} />
                    <div className="pb-expired" style={{ width: `${vTotal ? expiredN / vTotal * 100 : 0}%` }} />
                    <div className="pb-failed" style={{ width: `${vTotal ? failedN / vTotal * 100 : 0}%` }} />
                </div>
                <div className="meta">
                    {s.resolution || ""} {s.created_at || ""}
                    {aStored > 0 && ` | audio: ${aStored}/${aStored + aPending + aFailed} stored`}
                </div>
            </div>

            {expanded && (
                <div style={{ marginTop: 12, borderTop: "1px solid #333", paddingTop: 12 }}>
                    <ChunkMap
                        streamKey={s.stream_key}
                        title={displayName}
                        onSeek={playing ? (time: number) => playerSeek.current(time) : undefined}
                    />
                    {playing && (
                        <Player
                            streamKey={s.stream_key}
                            title={displayName}
                            isLive={isLive && s.type !== "dvr"}
                            seekRef={playerSeek}
                        />
                    )}
                    {isLive && <LiveAnnouncements streamKey={s.stream_key} />}
                </div>
            )}
        </div>
    );
}
